import { memo } from 'react';
import { styled } from '@mui/material';

import { Loader } from 'components/common/Loader';
import { FileItem } from 'components/user/Records/components/FileUploadsItem/FileItem';
import type { FileResponse } from 'types';

const PopoverGrid = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  padding: '12px',
  gap: '12px',
  backgroundColor: theme.palette.background.default,
  maxWidth: '680px',
  maxHeight: '360px',
  overflow: 'auto',
  '& > *': {
    flex: '0 0 180px',
    width: '180px',
    maxWidth: '180px',
  },
}));

const PlainGrid = styled('div')(() => ({
  display: 'contents',
}));

type FileCardsGridProps = {
  files: FileResponse[];
  isLoading?: boolean;
  disabledRemove?: boolean;
  skipStorageDelete?: boolean;
  onRemove?: (fileId: string) => void;
  /** popover = constrained hover preview layout; plain = cards only (parent owns layout) */
  variant?: 'popover' | 'plain';
  minWidth?: string | number;
  onOpenPreview?: () => void;
};

const FileCardsGridBase = ({
  files,
  isLoading = false,
  disabledRemove = false,
  skipStorageDelete = false,
  onRemove,
  variant = 'popover',
  minWidth,
  onOpenPreview,
}: FileCardsGridProps) => {
  const Wrapper = variant === 'plain' ? PlainGrid : PopoverGrid;

  if (isLoading) {
    return (
      <Wrapper sx={variant === 'popover' ? { minWidth } : undefined}>
        <Loader />
      </Wrapper>
    );
  }

  return (
    <Wrapper sx={variant === 'popover' ? { minWidth } : undefined}>
      {files.map((file) => (
        <FileItem
          key={file.id}
          file={file}
          disabledRemove={disabledRemove}
          skipStorageDelete={skipStorageDelete}
          onRemove={onRemove ? () => onRemove(file.id) : undefined}
          onOpenPreview={onOpenPreview}
        />
      ))}
    </Wrapper>
  );
};

export const FileCardsGrid = memo(FileCardsGridBase);
